/**
 * Double-tap a bare modifier to fire an action.
 *
 * The gesture is two clean taps of the same modifier: keydown → keyup with no
 * other key and no other modifier in between, each press released inside
 * `DOUBLE_TAP_MAX_HOLD_MS` and the second press starting inside
 * `DOUBLE_TAP_MAX_GAP_MS` of the first release.
 *
 * A *modifier* is the trigger for the same reason `usePushToTalk` holds one:
 * it inserts no character, so the gesture costs nothing to disambiguate from
 * typing in the very field it acts on. The two thresholds are what separate it
 * from everything else Control is used for — a chord (another key joins), a
 * push-to-talk hold (one press past 250ms), and an incidental tap (no second
 * one arrives). Both gestures can therefore listen to Control at once: a hold
 * is never two taps, and two taps are never a 400ms hold.
 *
 * All timing is measured with timers rather than clock reads so tests drive it
 * with fake timers, exactly as `usePushToTalk.test.ts` does.
 */
import { useEffect, useRef } from "react";

/**
 * How long a single press may last and still count as a tap. Above this the
 * user is holding the key, not tapping it — the press voids the gesture rather
 * than completing it, so a hold never lands as the first half of a double-tap.
 */
export const DOUBLE_TAP_MAX_HOLD_MS = 250;

/**
 * How long the second tap may take to arrive after the first is released.
 * Past this the taps are two unrelated presses.
 */
export const DOUBLE_TAP_MAX_GAP_MS = 400;

/**
 * Control on both platforms, for the same reason as `PUSH_TO_TALK_KEY`: it is
 * the one modifier that is neither the primary chord key on macOS (Command)
 * nor claimed by the OS when tapped bare (Alt focuses the Windows menu bar,
 * the Windows key opens the Start menu).
 */
export const DOUBLE_TAP_KEY = "Control";

const MODIFIER_FLAGS = ["ctrlKey", "shiftKey", "altKey", "metaKey"] as const;

/**
 * Each modifier key's own event flag, which is true on that key's own keydown
 * and therefore must not count as evidence of a chord.
 */
const OWN_MODIFIER_FLAG: Partial<
  Record<string, (typeof MODIFIER_FLAGS)[number]>
> = {
  Control: "ctrlKey",
  Shift: "shiftKey",
  Alt: "altKey",
  Meta: "metaKey",
};

interface UseDoubleTapKeyOptions {
  /** Gate the whole gesture — feature support, the surface being live. */
  enabled: boolean;
  /** `KeyboardEvent.key` to listen for. Defaults to `DOUBLE_TAP_KEY`. */
  key?: string;
  /**
   * Non-reactive gate — focus, ownership, anything that changes without a
   * re-render. Checked when each tap is armed and again before `onTrigger`
   * fires, since the gesture spans up to ~650ms.
   */
  canTrigger?: () => boolean;
  /** Fired once, on the keyup that completes the second tap. */
  onTrigger: () => void;
}

export function useDoubleTapKey({
  enabled,
  key = DOUBLE_TAP_KEY,
  canTrigger,
  onTrigger,
}: UseDoubleTapKeyOptions): void {
  // Handlers subscribe once and read current values through a ref, so a
  // re-render between the two taps cannot drop the keyup that completes the
  // gesture.
  const optsRef = useRef({ enabled, key, canTrigger, onTrigger });
  optsRef.current = { enabled, key, canTrigger, onTrigger };

  useEffect(() => {
    // Set while the trigger key is physically down and still a valid tap.
    let holdTimer: ReturnType<typeof setTimeout> | null = null;
    // Set between the first tap's release and the deadline for the second.
    let gapTimer: ReturnType<typeof setTimeout> | null = null;
    // Whether the current press is still a candidate tap.
    let pressed = false;
    // Completed taps so far: 0 or 1.
    let taps = 0;

    const clearTimers = () => {
      if (holdTimer !== null) {
        clearTimeout(holdTimer);
        holdTimer = null;
      }
      if (gapTimer !== null) {
        clearTimeout(gapTimer);
        gapTimer = null;
      }
    };

    /** Abandon the gesture entirely; the next bare press starts over. */
    const reset = () => {
      clearTimers();
      pressed = false;
      taps = 0;
    };

    const isAllowed = () => {
      const { enabled: on, canTrigger: allowed } = optsRef.current;
      return on && (allowed?.() ?? true);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== optsRef.current.key) {
        // Any other key means a chord or ordinary typing, not a double-tap —
        // both mid-press and in the gap between taps. Checked BEFORE the
        // repeat guard, mirroring `usePushToTalk`: a character key already
        // auto-repeating when the modifier goes down must still cancel.
        reset();
        return;
      }

      // Holding a key repeats keydown; only the first press arms a tap, and
      // the hold timer is what eventually voids a genuine hold.
      if (event.repeat) return;

      if (pressed) return;

      // A modifier already down makes this the tail of a chord (Shift then
      // Control), not a bare tap. The trigger key's own flag is exempt — it is
      // already true on that key's own keydown (ctrlKey on Control's, shiftKey
      // on Shift's), so checking it would veto every press of the key itself.
      const ownFlag = OWN_MODIFIER_FLAG[optsRef.current.key];
      if (MODIFIER_FLAGS.some((flag) => flag !== ownFlag && event[flag])) {
        reset();
        return;
      }

      if (!isAllowed()) {
        reset();
        return;
      }

      // The gap deadline is for the second press to START, and it has. Left
      // running, the timer would fire mid-press and void a tap whose release
      // lands past the deadline — an unhurried but valid double-tap would
      // silently do nothing.
      if (gapTimer !== null) {
        clearTimeout(gapTimer);
        gapTimer = null;
      }

      pressed = true;
      holdTimer = setTimeout(() => {
        // Held too long to be a tap. Void the whole gesture rather than just
        // this press, so a hold followed by a quick tap cannot fire.
        holdTimer = null;
        pressed = false;
        taps = 0;
      }, DOUBLE_TAP_MAX_HOLD_MS);
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.key !== optsRef.current.key) return;
      // The press was voided (too long, or stood down) — nothing to complete.
      if (!pressed) return;

      pressed = false;
      clearTimers();
      taps += 1;

      if (taps < 2) {
        gapTimer = setTimeout(() => {
          gapTimer = null;
          taps = 0;
        }, DOUBLE_TAP_MAX_GAP_MS);
        return;
      }

      taps = 0;
      // Re-checked rather than trusting the keydown-time result: the gesture
      // spans up to ~650ms, long enough for focus to move or the surface to
      // close under it.
      if (!isAllowed()) return;
      optsRef.current.onTrigger();
    };

    // A keyup delivered to another window never reaches us, and on macOS
    // Control+click is the secondary click — either would otherwise leave a
    // stale half-gesture armed for the next press to complete.
    const handleStandDown = () => reset();

    document.addEventListener("keydown", handleKeyDown, { capture: true });
    document.addEventListener("keyup", handleKeyUp, { capture: true });
    document.addEventListener("mousedown", handleStandDown, { capture: true });
    document.addEventListener("contextmenu", handleStandDown, {
      capture: true,
    });
    window.addEventListener("blur", handleStandDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
      document.removeEventListener("keyup", handleKeyUp, { capture: true });
      document.removeEventListener("mousedown", handleStandDown, {
        capture: true,
      });
      document.removeEventListener("contextmenu", handleStandDown, {
        capture: true,
      });
      window.removeEventListener("blur", handleStandDown);
      clearTimers();
    };
  }, []);
}
